import { Group, TextInput } from "@mantine/core";
import React from "react";
import { normalizeYearInput } from "../../utils/dateFormat";

// Helper function to validate month-year combination
export const validateMonthYear = (
  monthValue: string,
  yearValue: string,
  required: boolean = false,
  minYear: number = 1950,
  maxYear: number = new Date().getFullYear()
): string | null => {
  // If both are empty and not required, it's valid
  if (!monthValue && !yearValue && !required) return null;

  // If required but both are empty, it's invalid
  if (required && !monthValue && !yearValue) return "Date is required";

  // If one is filled but not the other, it's invalid
  if ((monthValue && !yearValue) || (!monthValue && yearValue)) {
    return "Both month and year are required";
  }

  // If both are filled, validate year range
  if (monthValue && yearValue) {
    const normalizedYear = normalizeYearInput(yearValue);
    if (normalizedYear.length === 4) {
      const year = parseInt(normalizedYear);
      if (year < minYear || year > maxYear) {
        return `Year must be between ${minYear} and ${maxYear}`;
      }
    }
  }

  return null;
};

interface MonthYearInputProps {
  monthValue: string;
  yearValue: string;
  onMonthChange: (value: string) => void;
  onYearChange: (value: string) => void;
  label: string;
  required?: boolean;
  disabled?: boolean;
  minYear?: number;
  maxYear?: number;
  showValidation?: boolean; // When true, shows validation errors
}

export function MonthYearInput({
  monthValue,
  yearValue,
  onMonthChange,
  onYearChange,
  label,
  required = false,
  disabled = false,
  minYear = 1950,
  maxYear = new Date().getFullYear(),
  showValidation = false,
}: MonthYearInputProps) {
  // Helper function to get validation errors
  const getMonthError = () => {
    if (!showValidation) return undefined;

    // If both are empty and not required, no error
    if (!monthValue && !yearValue && !required) return undefined;

    // If one is filled but not the other, show error
    if ((monthValue && !yearValue) || (!monthValue && yearValue)) {
      return "Both month and year are required";
    }

    return undefined;
  };

  const getYearError = () => {
    if (!showValidation) return undefined;

    // If both are empty and not required, no error
    if (!monthValue && !yearValue && !required) return undefined;

    // If one is filled but not the other, show error
    if ((monthValue && !yearValue) || (!monthValue && yearValue)) {
      return "Both month and year are required";
    }

    // Check year range only if year is complete (4 digits)
    const normalizedYear = normalizeYearInput(yearValue);
    if (normalizedYear.length === 4 &&
      (parseInt(normalizedYear) < minYear || parseInt(normalizedYear) > maxYear)) {
      return `Year must be between ${minYear} and ${maxYear}`;
    }

    return undefined;
  };

  return (
    <Group grow>
      <TextInput
        label={`${label} Month`}
        placeholder="MM"
        value={monthValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          if (
            value === "" ||
            (/^\d{1,2}$/.test(value) &&
              parseInt(value) >= 1 &&
              parseInt(value) <= 12)
          ) {
            onMonthChange(value);
          }
        }}
        required={required}
        disabled={disabled}
        error={getMonthError()}
      />
      <TextInput
        label={`${label} Year`}
        placeholder="YYYY"
        value={yearValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          // Allow any numeric input up to 4 digits
          if (value === "" || (value.length <= 4 && /^\d*$/.test(value))) {
            onYearChange(value);
          }
        }}
        required={required}
        disabled={disabled}
        error={getYearError()}
        onBlur={() => {
          if (!yearValue) {
            return;
          }

          const normalizedYear = normalizeYearInput(yearValue);
          if (normalizedYear !== yearValue) {
            onYearChange(normalizedYear);
          }
        }}
      />
    </Group>
  );
}
